import { useCallback, useEffect, useRef, useState, type CSSProperties } from "react";
import { Button } from "@legends/ui";

// Broadcast resilience (ADR 0042). Two cross-surface concerns live here so the
// Fan (ConsumerWatch), Legend (GoLivePanel) and Director (DirectorConsole) all
// behave the same:
//
//  1. STABLE PROGRAM IDENTITY — `program.source` stores a publisher's STABLE key
//     (their underlying username), carried on the LiveKit participant Name, not
//     the per-join random identity `username#<rand>` (ADR 0027). So when a
//     publisher drops and rejoins with a fresh identity but the SAME name, the
//     program auto-restores to them with no director action.
//
//  2. RECONNECT MESSAGING — a non-blocking banner reflects the LiveKit
//     connection state (Reconnecting → Reconnected → lost) with an accessible
//     live region + a rejoin affordance on hard disconnect.

/** A LiveKit participant's STABLE program key — the underlying username. Prefers
 *  the participant Name (set by the token minter, ADR 0042); falls back to the
 *  identity prefix before '#' so it still resolves when Name is absent. */
export function participantSourceKey(p: { name?: string; identity?: string }): string {
  const name = (p.name ?? "").trim();
  if (name) return name;
  const id = p.identity ?? "";
  const hash = id.indexOf("#");
  return hash >= 0 ? id.slice(0, hash) : id;
}

/** The live media kind of the on-program source (ADR 0043): a live video track
 *  → "video"; mic only, no live video → "audio" (an intentional audio-led
 *  CaveCast); nothing live → "none" (involuntary signal loss, ADR 0042).
 *  Audio-only IS live — it must never be treated as "signal lost". */
export type SourceKind = "video" | "audio" | "none";

/** The stable key for the local publisher, from its own unique identity. */
export function selfSourceKey(identity: string | null | undefined): string | null {
  if (!identity) return null;
  const hash = identity.indexOf("#");
  return hash >= 0 ? identity.slice(0, hash) : identity;
}

/** Whether a LiveKit participant identity is an ADR 0044 guest publisher, minted
 *  as `guest-<slug>#<rand>`. Used to default the Legend to slot A and a guest to
 *  slot B in a multi-head layout, and to keep guests off the single-source
 *  Legend default (ADR 0062). Operators join as `<username>#<rand>`. */
export function isGuestIdentity(identity: string | null | undefined): boolean {
  return !!identity && identity.startsWith("guest-");
}

export type ConnState = "live" | "reconnecting" | "reconnected" | "lost";

/**
 * Track the LiveKit connection state. Setting it to "reconnected" auto-clears
 * back to "live" after a short confirmation window; the timer is cleaned up on
 * unmount so it never updates a torn-down surface.
 */
export function useConnState(initial: ConnState = "live") {
  const [state, setStateRaw] = useState<ConnState>(initial);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clear = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };
  const setState = useCallback((next: ConnState) => {
    clear();
    setStateRaw(next);
    if (next === "reconnected") {
      timerRef.current = setTimeout(
        () => setStateRaw((cur) => (cur === "reconnected" ? "live" : cur)),
        3000,
      );
    }
  }, []);
  useEffect(() => clear, []);
  return [state, setState] as const;
}

/** Structural slice of a LiveKit Room's localParticipant — just enough of the
 *  publish-control surface for {@link useBackgroundMediaSuspend} and a test fake.
 *  The `is*Enabled` getters are read to capture the pre-background state; the
 *  `set*Enabled` setters suspend + restore it. */
export interface SuspendableLocalParticipant {
  readonly isMicrophoneEnabled?: boolean;
  readonly isCameraEnabled?: boolean;
  setMicrophoneEnabled(enabled: boolean): Promise<unknown> | unknown;
  setCameraEnabled(enabled: boolean): Promise<unknown> | unknown;
}
export interface SuspendableRoom {
  localParticipant: SuspendableLocalParticipant;
}

/**
 * Stop publishing a guest's microphone AND camera while the tab is backgrounded,
 * and restore the prior state on return (ADR 0227, LSC-506).
 *
 * A backgrounded tab's camera track stops on its own, but the MICROPHONE keeps
 * capturing and publishing — so a guest who locks their phone / switches apps /
 * navigates away is still HEARD, with no video to signal they're still live. This
 * hook closes that audio leak symmetrically for audio + video:
 *
 *  - On `visibilitychange` → hidden (and `pagehide`), it captures the current
 *    mic/camera enabled state, then disables BOTH published tracks — nothing is
 *    published while hidden, and the publish state reflects the truth.
 *  - On `visibilitychange` → visible, it restores EXACTLY the captured prior state
 *    (so a guest who had manually muted stays muted — we never force-enable).
 *
 * Scoped to the guest publisher surface (the reported case). The Legend Go-Live
 * surface deliberately keeps publishing when backgrounded (the chroma compositor's
 * worker-clock, ADR 0102, is built to), so it does NOT use this. Everything is
 * best-effort and wrapped in try/catch — a background cycle never crashes the
 * surface. A null room (not yet live) is a no-op.
 */
export function useBackgroundMediaSuspend(room: SuspendableRoom | null): void {
  // Persist across effect re-runs (e.g. a room swap): whether we're currently
  // suspended, and the mic/camera enabled state captured at suspend time.
  const suspendedRef = useRef(false);
  const priorRef = useRef<{ mic: boolean; cam: boolean }>({ mic: true, cam: true });

  useEffect(() => {
    if (!room) return;
    const lp = room.localParticipant;
    // A fresh room = a fresh publish session (first connect, or a rejoin, which
    // re-enables mic+camera). Start un-suspended so the next real hide captures the
    // new session's state rather than reusing a stale latch/prior from a dropped one.
    suspendedRef.current = false;

    const set = (fn: (v: boolean) => Promise<unknown> | unknown, v: boolean) => {
      try {
        Promise.resolve(fn.call(lp, v)).catch(() => {});
      } catch {
        // Never let a background/restore crash the surface.
      }
    };

    const suspend = () => {
      if (suspendedRef.current) return; // idempotent — pagehide + hidden can both fire
      suspendedRef.current = true;
      // Capture the TRUE pre-background state so restore is exact (a guest who had
      // muted their mic must stay muted, not be force-enabled on return).
      priorRef.current = {
        mic: lp.isMicrophoneEnabled ?? true,
        cam: lp.isCameraEnabled ?? true,
      };
      set(lp.setMicrophoneEnabled, false);
      set(lp.setCameraEnabled, false);
    };

    const restore = () => {
      if (!suspendedRef.current) return;
      suspendedRef.current = false;
      const prior = priorRef.current;
      set(lp.setMicrophoneEnabled, prior.mic);
      set(lp.setCameraEnabled, prior.cam);
    };

    const onVisibility = () => {
      if (document.visibilityState === "hidden") suspend();
      else restore();
    };
    const onPageHide = () => suspend();

    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", onPageHide);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", onPageHide);
    };
  }, [room]);
}

const bannerBase: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  padding: "8px 12px",
  borderRadius: "var(--radius-md)",
  fontFamily: "var(--font-mono)",
  fontSize: "var(--fs-2xs)",
  letterSpacing: "var(--ls-label)",
};

/** A non-blocking, accessible connection banner. Renders nothing when live. */
export function ConnectionBanner({ state, onRejoin }: { state: ConnState; onRejoin?: () => void }) {
  if (state === "live") return null;
  const text =
    state === "reconnecting"
      ? "Reconnecting…"
      : state === "reconnected"
        ? "Reconnected"
        : "Connection lost";
  const accent =
    state === "lost" ? "var(--alert-2)" : state === "reconnected" ? "var(--success-hi)" : "var(--ember)";
  return (
    <div
      role="status"
      aria-live="polite"
      style={{ ...bannerBase, border: `1px solid ${accent}`, color: "var(--text-hi)", background: "var(--surface-2)" }}
    >
      <span aria-hidden style={{ width: 8, height: 8, borderRadius: "50%", background: accent }} />
      <span style={{ color: accent }}>{text}</span>
      {state === "lost" && onRejoin && (
        <div style={{ marginLeft: "auto" }}>
          <Button size="sm" variant="accent" aria-label="Rejoin" onClick={onRejoin}>
            Rejoin
          </Button>
        </div>
      )}
    </div>
  );
}
